import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import classnames from 'classnames';
import css from './css/AddCustomQuestionModal.scss';
import commonCss from './css/SurveyStyle.scss';
import Header from 'common/Header';
import Footer from 'common/Footer';
import StyledButton from 'common/StyledButton';
import ErrorMessage from 'common/ErrorMessage';
import AddCustomQuestionForm from './AddCustomQuestionForm';
import { merge } from 'lodash';
import { isEmptyString } from 'utils/text';
import PropTypes from 'prop-types';
import useStarfishAPI from 'hooks/useStarfishAPI';
import striptags from 'striptags';
import Toast from 'common/Toast';
import {getWord} from 'utils/tenant';

const initialDataState = {
    id: null,
    questionText: '',
    questionType: null,
    questionOptions: [],
    editable: true
};

const initialErrorState = {
    formError: null,
    questionTextError: null,
    questionTypeError: null,
    questionOptionsError: null
};

const AddCustomQuestionModal = ({isOpen, closeModal, questionIdToEdit, isEdit, onSave}) => {
    Modal.setAppElement(document.getElementById('main-center'));

    const [getQuestionResponse, getQuestionAPI] = useStarfishAPI({ isNewAPI: true });
    const [submitNewQuestionResponse, submitNewQuestionAPI] = useStarfishAPI({ isNewAPI: true });
    const [formData, setFormData] = useState(initialDataState);
    const [isOptionsQuestion, setIsOptionsQuestion] = useState(false);
    const [errors, setErrors] = useState(initialErrorState);
    const [toastMessage, setToastMessage] = useState(null);

    const hasOptions = (questionType) => questionType === 'CHECKBOX_SELECT' || questionType === 'RADIO_SELECT';

    const setNewQuestionData = (newQuestionFields) => {
        if (newQuestionFields.questionType) {
            setIsOptionsQuestion(hasOptions(newQuestionFields.questionType.value));
        }

        setFormData(prevData => ({...prevData, ...newQuestionFields}));
    };

    let formHasError = false;

    const updateErrors = (changedError) => {
        if (Object.values(changedError).some(Boolean)) {
            formHasError = true;
        }

        setErrors(prevErrors => {
            let combinedErrors = {...prevErrors, formError: null, ...changedError};
            if (!combinedErrors.formError && Object.values(combinedErrors).some(Boolean)) {
                combinedErrors = merge({}, combinedErrors, {formError: 'Complete any missing required fields.'});
            }

            return combinedErrors;
        });
    };

    const fetchQuestion = useCallback((questionId) => {
        getQuestionAPI(`../api/student-survey-questions/${questionId}`, { method: 'GET' });
    }, []);

    useEffect(() => {
        if (getQuestionResponse.success) {
            setFormData(getQuestionResponse.data);
            setIsOptionsQuestion(hasOptions(getQuestionResponse.data.questionType.value))
        } else if (!getQuestionResponse.pending && getQuestionResponse.error) {
            updateErrors({formError: `There was an error processing your request. \
                If this error continues to appear, please contact Starfish support.`});
        }
    }, [getQuestionResponse]);

    useEffect(() => {
        if (questionIdToEdit) fetchQuestion(questionIdToEdit)
    }, [questionIdToEdit]);

    const submit = useCallback(() => {
        submitNewQuestionAPI('../api/student-survey-questions', {
            method: isEdit ? 'PUT' : 'POST',
            body: JSON.stringify({
                id: formData.id,
                questionText: striptags(formData.questionText),
                questionType: formData.questionType.value,
                questionOptions: isOptionsQuestion ? formData.questionOptions.map(option =>
                    ({text: striptags(option.text), position: option.position})
                ) : []
            }),
            headers: {'Content-Type': 'application/json; charset=UTF-8'}
        });
    }, [formData]);

    const validate = () => {
        if (isEmptyString(formData.questionText)) {
            updateErrors({questionTextError: 'Provide question text.'});
        }
        if (!formData.questionType) {
            updateErrors({questionTypeError: 'Select a question type.'});
        }
        if (isOptionsQuestion && !formData.questionOptions.length) {
            updateErrors({questionOptionsError: 'Add at least one question option.'});
        }

        if (!formHasError) {
            submit();
        }
    }

    const resetModal = () => {
        setFormData(initialDataState);
        setErrors(initialErrorState);
        setIsOptionsQuestion(false);
        closeModal();
    };

    useEffect(() => {
        if (submitNewQuestionResponse.success) {
            resetModal();
            onSave();
        } else if (!submitNewQuestionResponse.pending && submitNewQuestionResponse.error) {
            updateErrors({formError: `There was an error processing your request. \
                If this error continues to appear, please contact Starfish support.`});
        }
    }, [submitNewQuestionResponse]);

    return <>
        <Modal
            isOpen={isOpen}
            onRequestClose={resetModal}
            overlayClassName={css['overlay']}
            className={css['modal']}
            shouldCloseOnOverlayClick={false}
            contentLabel={`${isEdit ? 'Edit' : 'Add'} Custom Question`}
        >
            <div className={css['container']}>
                <Header closeModal={resetModal} />
                {errors.formError && <ErrorMessage message={errors.formError} />}

                <div className={css['content']}>
                    <h2 className={commonCss['header-second-level']}>{isEdit ? 'Edit' : 'Add'} Question</h2>
                    <div className={classnames(commonCss['instructional-text'], commonCss['spacing-below'])}>
                        Type your question and select the question type. The question cannot be edited once it is part
                        of at least one scheduled, open, or closed {getWord('studentSurvey', false)}. HTML is not accepted here.
                    </div>
                    <div className={classnames(css['required'], commonCss['spacing-below'])}>
                        <span className={commonCss['required-asterisk']} aria-hidden>*</span> Required Fields
                    </div>

                    <AddCustomQuestionForm formData={formData}
                                           setNewQuestionData={setNewQuestionData}
                                           isOptionsQuestion={isOptionsQuestion}
                                           errors={errors}
                                           updateErrors={updateErrors}
                                           setToastMessage={setToastMessage} />
                </div>

                <Footer
                    primaryButton={<StyledButton onClick={validate}>Save Question</StyledButton>}
                    secondaryButton={<StyledButton asLink onClick={resetModal}>Cancel</StyledButton>}
                />
            </div>
        </Modal>
        {!!toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage(null)} />}
    </>;
};

AddCustomQuestionModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    questionIdToEdit: PropTypes.number,
    isEdit: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired
};

export default AddCustomQuestionModal;
